import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { getPaginatedCampaigns } from "../../../api/Campaigns";
import { urlifyName } from "../../shared/Utils/PageUrl";
import CampaignCard from "../CampaignCard/CampaignCard";
import Spinner from "../../shared/LoadingSpinner/Spinner";
import { states } from "states-us";
import SearchHeader from "./SearchHeader";
import PaginationButtons from "../../shared/Buttons/pagination";

// This page is the search page to render 3 different types of campaigns
// Grabs the campaign data from API depending on the URL pathname
// Renders a different header depending on the type

const ageOptions = [
  { value: -1, label: "All Ages" },
  { value: 1, label: "Newborn" },
  { value: 2, label: "Young" },
  { value: 3, label: "Adult" },
  { value: 4, label: "Senior" },
  { value: 5, label: "Unknown" },
];

// productTypeOptions commented out - shop feature disabled
// const productTypeOptions = [
//   { value: "all", label: "All" },
//   { value: "food", label: "Food" },
//   { value: "toys", label: "Toys" },
//   { value: "supplies", label: "Supplies" },
//   { value: "beds", label: "Beds" },
//   { value: "medical", label: "Medical Equipment" },
//   { value: "other", label: "Other" },
// ];

const projectStatusOptions = [
  { value: "all", label: "All Statuses" },
  { value: "planning", label: "Planning" },
  { value: "construction", label: "Under Construction" },
  { value: "completed", label: "Completed" },
];

export const campaignExcerpt = (plainText) => {
  const EXCERPT_CHARS = 45;
  const EXCERPT_SENTENCES = 1;
  let sentences =
    plainText.split(".").slice(0, EXCERPT_SENTENCES).join(". ") + ".";
  return sentences.length > EXCERPT_CHARS
    ? sentences.substring(0, EXCERPT_CHARS) + "..."
    : sentences;
};

export default function CampaignSearch() {
  // States used by all campaigns
  const [campaigns, setCampaigns] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [stateParam, setStateParam] = useState("");
  const [type, setType] = useState("");

  // Search states for different campaigns
  const [animalSearch, setAnimalSearch] = useState({
    search: "",
    species: "",
    age: -1,
    state: "",
    nonprofitId: "",
  });
  // productSearch commented out - shop feature disabled
  // const [productSearch, setProductSearch] = useState({
  //   search: "",
  //   state: "",
  //   nonprofitId: "",
  //   productType: "all",
  // });
  const [capitalSearch, setCapitalSearch] = useState({
    search: "", // includes land area, nonprofit
    state: "",
    nonprofitId: "",
    projectStatus: "all",
    completionDate: "all", // All, This week, this month, within 6 months, within a year, 1+ more
  });

  // States for the location (campaign type) and navigation (campaign feature page)
  const location = useLocation();
  const navigate = useNavigate();

  // API call to get campaigns
  // Gets called on first render, every change in search, and page change
  const getAllCampaigns = async (page, searches, type) => {
    try {
      setIsLoading(true);
      const response = await getPaginatedCampaigns(page, searches, type);

      if (response) {
        setIsLoading(false);
        // Ensure campaigns is always an array, even if response.results is undefined
        setCampaigns(response.results || []);

        // Calculate total pages (assuming 12 campaigns per page)
        const totalCount = response.count || 0;
        const calculatedTotalPages = Math.ceil(totalCount / 12);
        setTotalPages(calculatedTotalPages);
      } else {
        // Handle case where response is null/undefined
        setIsLoading(false);
        setCampaigns([]);
        setTotalPages(1);
      }
    } catch (error) {
      console.error(`Could not get campaigns`, error);
      setIsLoading(false);
      setCampaigns([]);
      setTotalPages(1);
    }
  };

  // Tracks changes to the URL and sets the campaign type
  // Grabs data based on the type
  useEffect(() => {
    const pathSegments = location.pathname.split("/");
    const campaignType = pathSegments[2];
    setType(campaignType);

    // Clear campaigns and searches when switching types
    setCampaigns([]);
    setAnimalSearch({
      search: "",
      species: "",
      age: -1,
      state: "",
      nonprofitId: "",
    });
    // setProductSearch({ // Commented out - shop feature disabled
    //   search: "",
    //   state: "",
    //   nonprofitId: "",
    //   productType: "all",
    // });
    setCapitalSearch({
      search: "",
      state: "",
      nonprofitId: "",
      projectStatus: "all",
      completionDate: "all",
    });

    if (campaignType === "animal") {
      getAllCampaigns(1, animalSearch, "animal");
    // } else if (campaignType === "product") { // Commented out - shop feature disabled
    //   getAllCampaigns(1, productSearch, "product");
    } else if (campaignType === "capital") {
      getAllCampaigns(1, capitalSearch, "capital");
    }
    setPage(1);
  }, [location.pathname]);

  // Handle key down events (search on Enter key)
  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      let searchValues;
      if (type === "animal") {
        searchValues = animalSearch;
      // } else if (type === "product") { // Commented out - shop feature disabled
      //   searchValues = productSearch;
      } else if (type === "capital") {
        searchValues = capitalSearch;
      }
      handleSearch(searchValues);
    }
  };

  // Search when search term becomes empty
  useEffect(() => {
    const getCurrentSearch = () => {
      if (type === "animal") return animalSearch;
      // if (type === "product") return productSearch; // Commented out - shop feature disabled
      if (type === "capital") return capitalSearch;
      return null;
    };

    const currentSearch = getCurrentSearch();
    if (currentSearch && currentSearch.search === "") {
      handleSearch(currentSearch);
    }
  }, [animalSearch.search, capitalSearch.search, type]);

  // Auto-search when non-text filters change (state, age, species, productType, projectStatus)
  useEffect(() => {
    if (type === "animal") {
      getAllCampaigns(1, animalSearch, "animal");
    // } else if (type === "product") { // Commented out - shop feature disabled
    //   getAllCampaigns(1, productSearch, "product");
    } else if (type === "capital") {
      getAllCampaigns(1, capitalSearch, "capital");
    }
    setPage(1);
  }, [
    animalSearch.species,
    animalSearch.age,
    animalSearch.state,
    animalSearch.nonprofitId,
    // productSearch.productType, // Commented out - shop feature disabled
    // productSearch.state,
    // productSearch.nonprofitId,
    capitalSearch.projectStatus,
    capitalSearch.state,
    capitalSearch.nonprofitId,
    type,
  ]);

  // Scrolls to the top of the page only on first render
  useEffect(() => {
    if (page === 1) {
      window.scrollTo(0, 0);
    }
  }, [page]);

  // Handles clicking on 'Search' button
  const handleSearch = (values) => {
    setPage(1);
    getAllCampaigns(1, values, type);
  };

  const optionsMaps = {
    // productType: productTypeOptions, // Commented out - shop feature disabled
    projectStatus: projectStatusOptions,
  };

  // Handles attached searches to the campaign state
  const handleSearchChange = (e) => {
    const { name, value } = e.target;
    const finalValue =
      name === "age"
        ? value === -1
          ? -1
          : value
        : optionsMaps[name]
        ? optionsMaps[name].find((option) => option.value === value)?.value ||
          value
        : value;

    if (type === "animal") {
      setAnimalSearch((prevState) => ({
        ...prevState,
        [name]: finalValue,
      }));
    // } else if (type === "product") { // Commented out - shop feature disabled
    //   setProductSearch((prevState) => ({
    //     ...prevState,
    //     [name]: value,
    //   }));
    } else if (type === "capital") {
      setCapitalSearch((prevState) => ({
        ...prevState,
        [name]: value,
      }));
    }
  };

  // Redirects to the Campagin Feature page
  const handleClick = (campaign) => {
    if (type === "animal") {
      navigate(
        `/campaigns/animal/${urlifyName(campaign.nonprofit_name)}/${urlifyName(
          campaign.animal_name
        )}/${campaign.id}`
      );
    } else if (type === "capital") {
      navigate(
        `/campaigns/${type}/${urlifyName(campaign.nonprofit_name)}/${urlifyName(
          campaign.campaign_title
        )}/${campaign.id}`
      );
    }
  };

  // Handles page change
  const handlePageChange = (newPage) => {
    setPage(newPage);
    window.scrollTo(0, 0);

    if (type === "animal") {
      getAllCampaigns(newPage, animalSearch, "animal");
    // } else if (type === "product") { // Commented out - shop feature disabled
    //   getAllCampaigns(newPage, productSearch, "product");
    } else if (type === "capital") {
      getAllCampaigns(newPage, capitalSearch, "capital");
    }
  };

  return (
    <div>
      <SearchHeader
        handleSearchChange={handleSearchChange}
        animalSearch={animalSearch}
        ageOptions={ageOptions}
        productSearch={null}
        productTypeOptions={null}
        capitalSearch={capitalSearch}
        projectStatusOptions={projectStatusOptions}
        type={type}
        handleSearch={handleSearch}
        handleKeyDown={handleKeyDown}
        stateParam={stateParam}
        states={states}
      />

      <div className="flex flex-col my-8">
        {isLoading ? (
          <div className="flex flex-row mx-auto mt-16 w-full pb-24 justify-center">
            <Spinner />
          </div>
        ) : (
          <>
            <div className="w-full grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 max-w-7xl mx-auto px-4">
              {campaigns && campaigns.length > 0 ? (
                campaigns.map((campaign) => (
                  <CampaignCard
                    key={campaign.id}
                    campaign={campaign}
                    handleClick={handleClick}
                  />
                ))
              ) : (
                <div className="col-span-full text-center py-8">
                  <p>Sorry, no matching results!</p>
                </div>
              )}
            </div>

            {!isLoading && campaigns.length > 0 && totalPages > 1 && (
              <PaginationButtons
                currentPage={page}
                totalPages={totalPages}
                onPageChange={handlePageChange}
                className="mt-16"
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}
